<template>
  <div
    :class="[$style.sbCheckbox, disabled ? '' : $style['sbCheckbox-' + variation]]"
    data-sb-regression="sb-checkbox"
  >
    <label
      :class="labelClass"
    >
      <div :class="$style.checkmarkWrapper">
        <Checkbox
          :class="[$style.formCheckInput, disabled && $style['formCheckInput-disabled']]"
          input-class="sb-checkbox"
          :model-value="canCheck"
          :binary="true"
          :disabled="disabled"
          :size="'small'"
          v-bind="$attrs"
          :pt:box:class="$style.checkboxBox"
          @update:modelValue="inputChanged"
        >
          <template #icon>
            <i
              aria-hidden="true"
              :class="checkmarkClasses"
            />

          </template>
        </Checkbox>

        <div
          v-if="$slots.default"
          :class="$style.labelSlot"
          data-sb-automation="sb-checkbox-label-slot"
        >
          <!--
              @slot text, components, or HtmlElements as children of the component
            -->
          <slot />
        </div>
      </div>
    </label>
  </div>
</template>

<script setup>
/* eslint-disable css-modules/no-undef-class */
import Checkbox from 'primevue/checkbox';
import { defineProps, defineEmits, computed, defineOptions, useCssModule } from 'vue';
import { isEqual } from 'lodash';

const $style = useCssModule();
defineOptions({
  name: 'SbCheckboxNext',
});

const props = defineProps(
  {
    /**
       * It is the type that the checkbox is modeled on. You use it as v-model="putYourObjHere" that you want it to work from.
       */
    checked: {
      type: [String, Boolean, Object, Array],
      default: null,
    },
    /**
       * Check state is neither true nor false, used for check/uncheck all status for table headers
       */
    indeterminate: {
      type: Boolean,
      default: false,
    },
    /**
       * It is used to store the value of the checkbox so it can be saved in the model
       */
    value: {
      type: [String, Boolean, Object, Number],
      default: null,
    },
    /**
       * Style variation to give additional meaning.
       * `primary, secondary, tertiary`
       */
    variation: {
      type: String,
      default: 'primary',
      validator: (value) => {
        return value.match(/(primary|secondary|tertiary)/);
      },
    },
    /**
       * toggles checkbox's disabled state
       */
    disabled: {
      type: Boolean,
      default: false,
    },
  },
);

const emit = defineEmits(
  [
    /**
           * Emmited when a dismissable chip is closed.
           */
    'update:checked',
  ],
);

const canCheck = computed(() => {
  if (Array.isArray(props.checked)) {
    return props.checked.some((v) => isEqual(props.value, v));
  }
  return props.checked;
});
const checkmarkClasses = computed(() => {
  return [$style.checkmark,
    canCheck.value && $style['checkmark-checked'],
    canCheck.value && $style[`checkmark-checked-${props.variation}`],
    props.indeterminate && $style['checkmark-indeterminate'],
    props.disabled && $style['checkmark-disabled'],
  ].filter(Boolean);
});
const labelClass = computed(() => {
  return [$style.formCheckLabel, props.disabled && $style['formCheckLabel-disabled'], props.indeterminate && $style['formCheckLabel-indeterminate']].filter(Boolean);
});
function emitNewArray() {
  const arrCopy = props.checked.slice();
  if (arrCopy.some((v) => isEqual(props.value, v))) {
    arrCopy.splice(arrCopy.findIndex((v) => isEqual(props.value, v)), 1);
  } else {
    arrCopy.push(props.value);
  }
  emit('update:checked', arrCopy);
}

function inputChanged() {
  if (Array.isArray(props.checked)) emitNewArray();
  else if (Object.hasOwn(props, 'value')) emit('update:checked', props.value ?? props.checked);
  else emit('update:checked', !props.checked);
}

</script>
<style lang="scss" module>
@use '@src/components/Checkbox/style/sb-checkbox.module.scss';
</style>
